<template>
  <div class="Channels">
    <div
      v-if="activeChannels.length > 0"
      class="ChannelsSection--Channels ChannelsSection--Channels__Active"
    >
      <div
        v-for="(channel, index) in activeChannels"
        :key="index"
        class="Channels--Channel"
        :class="getCornerClass('active', index)"
        :data-qa="`active-channel-${channel.toLowerCase().replace(/_/g, '-')}`"
      >
        <div class="Channel--Info">
          <icon
            v-if="channels[channel].icon"
            size="30"
            :glyph="channels[channel].icon"
          />
          <div class="ChannelInfo--TitleLine">
            <div class="ChannelInfo--Title">{{ channels[channel].title }}</div>
            <el-switch
              v-if="channels[channel].toggleable"
              :value="channelsState[channel]"
              @change="toggleChannel($event, channel)"
              :data-qa="`channel-toggle-${channel}`"
            ></el-switch>
          </div>
          <div v-if="isSubtitleExists(channel)" class="ChannelInfo--Subtitle">
            {{ getSubtitleForChannel(channel) }}
          </div>
        </div>
        <div
          v-if="channels[channel].activeStatus && channels[channel].count > 0"
          class="Channel--Status"
        >
          <el-badge is-dot class="Channel--Status__Active" />
          <span>{{ channels[channel].activeStatus }}</span>
        </div>
        <div class="Channel--Actions">
          <el-button
            v-if="displayManageButton(channel)"
            plain
            type="primary"
            size="small"
            @click="manageButtonClick(channel)"
            :data-qa="
              `manage-button-${channel.toLowerCase().replace(/_/g, '-')}`
            "
          >
            {{ $t('Channels_ManageInAppsAndSites') }}
            <i class="el-icon-right"></i>
          </el-button>
          <el-button
            v-if="displaySettingsButton(channel)"
            plain
            type="primary"
            size="small"
            @click="manageSettingsClick(channel)"
            icon="el-icon-setting"
            :data-qa="
              `settings-button-${channel.toLowerCase().replace(/_/g, '-')}`
            "
          >
            {{ $t('Channels_Settings') }}
          </el-button>
        </div>
      </div>
    </div>
    <div class="ChannelsSection" v-if="inactiveChannels.length > 0">
      <span class="ChannelsSection--Title">
        {{ $t('Channels_DiscoverMoreChannels') }}
      </span>
      <div
        class="ChannelsSection--Channels ChannelsSection--Channels__Inactive"
      >
        <div
          v-for="(channel, index) in inactiveChannels"
          :key="index"
          class="Channels--Channel"
          :class="getCornerClass('inactive', index)"
        >
          <div class="Channel--Info">
            <icon
              v-if="channels[channel].icon"
              size="30"
              :glyph="channels[channel].icon"
            />
            <div class="ChannelInfo--Title">{{ channels[channel].title }}</div>
            <div class="Channel--Actions">
              <span v-if="isChannelRequested(channel)">{{
                $t('ApplicationsChannels_RequestIsSent')
              }}</span>
              <el-button
                v-else
                plain
                size="small"
                :loading="isChannelInProgress(channel)"
                :disabled="isChannelInProgress(channel)"
                @click="requestChannel(channel)"
              >
                {{ $t('ApplicationsChannels_TalkToSales') }}
                <i class="el-icon-right"></i>
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { emailVerificationEnum } from '@/modules/settings/enum/EmailVerificationEnum'
import { channelRequestStatusEnum } from '@/modules/settings/enum/ChannelRequestStatusEnum'
import { projectRoute } from '@/core/helpers'

export default {
  name: 'ChannelsSection',

  data() {
    return {
      emailVerificationEnum,
      channelRequestStatusEnum
    }
  },

  props: {
    channels: {
      type: Object,
      required: true
    },
    channelsState: {
      type: Object,
      required: true
    },
    channelsInProgress: {
      type: Array,
      required: true
    }
  },

  computed: {
    activeChannels() {
      if (this.channels.length === 0) return []

      return Object.keys(this.channels).filter(
        channel => this.channels[channel].available
      )
    },

    inactiveChannels() {
      if (this.channels.length === 0) return []

      return Object.keys(this.channels).filter(
        channel => !this.channels[channel].available
      )
    },

    isVerificationSent() {
      return (
        this.channelsState.email &&
        this.channelsState.email_verification === emailVerificationEnum.SENT
      )
    }
  },

  methods: {
    toggleChannel(value, channel) {
      this.$emit('toggle', { channel: channel, value: value })
    },

    displayManageButton(channel) {
      switch (channel) {
        case 'app_push':
        case 'web_push':
        case 'inbox':
        case 'inapp':
        case 'onsite':
          return true
        default:
          return false
      }
    },

    isSubtitleExists(channel) {
      switch (channel) {
        case 'email':
          return this.isVerificationSent
        default:
          return false
      }
    },

    getSubtitleForChannel(channel) {
      switch (channel) {
        case 'email':
          return this.$t('ProjectSettings_ChannelSettingsTab_VerificationSent')
        default:
          return null
      }
    },

    displaySettingsButton(channel) {
      switch (channel) {
        case 'inbox':
          return this.channels[channel].count > 0
        case 'email':
        case 'sms':
          return this.channelsState[channel]
        default:
          return false
      }
    },

    manageButtonClick(channel) {
      switch (channel) {
        case 'app_push':
        case 'web_push':
        case 'inbox':
        case 'inapp':
        case 'onsite':
          this.$router.push(
            projectRoute({
              name: 'Applications'
            })
          )
          break
        default:
          return false
      }
    },

    manageSettingsClick(channel) {
      switch (channel) {
        case 'inbox':
          this.$router.push(
            projectRoute({
              name: 'InboxSettings'
            })
          )
          break
        case 'email':
          this.$router.push(
            projectRoute({
              name: 'EmailSettings'
            })
          )
          break
        case 'sms':
          this.$router.push(
            projectRoute({
              name: 'SmsSettings'
            })
          )
          break
        default:
          return false
      }
    },

    isChannelInProgress(channel) {
      return this.channelsInProgress.includes(channel)
    },

    isChannelRequested(channel) {
      return (
        this.channels[channel].requestStatus === channelRequestStatusEnum.NEW ||
        this.channels[channel].requestStatus ===
          channelRequestStatusEnum.IN_PROCESS
      )
    },

    requestChannel(channel) {
      this.$emit('requestChannel', channel)
    },

    getCornerClass(type, index) {
      const total =
        type === 'active'
          ? this.activeChannels.length
          : this.inactiveChannels.length

      const rows = Math.ceil(total / 3)

      return {
        'Channels--Channel__RoundedTopLeft': index === 0,
        'Channels--Channel__RoundedTopRight': index === 2,
        'Channels--Channel__RoundedBottomLeft': index === (rows - 1) * 3,
        'Channels--Channel__RoundedBottomRight': index === (rows - 1) * 3 + 2
      }
    }
  }
}
</script>

<style scoped>
.Channels {
  padding: 10px;
}

.ChannelsSection--Channels {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 1px;
}

.ChannelsSection--Channels__Active .Channel--Info .icon {
  color: #51ade6;
}

.ChannelsSection--Channels__Inactive .Channel--Info .icon {
  color: #a7acba;
}

.ChannelsSection {
  margin-top: 40px;
}

.Channels--Channel {
  display: flex;
  flex-wrap: wrap;
  height: 130px;
  padding: 24px;
  background-color: #ffffff;
  box-shadow: 0 0 0 1px #dbdee7;
}

.Channels--Channel__RoundedTopLeft {
  border-top-left-radius: 4px;
}

.Channels--Channel__RoundedTopRight {
  border-top-right-radius: 4px;
}

.Channels--Channel__RoundedBottomLeft {
  border-bottom-left-radius: 4px;
}

.Channels--Channel__RoundedBottomRight {
  border-bottom-right-radius: 4px;
}

.Channel--Info {
  flex-basis: 50%;
}

.ChannelsSection--Title {
  display: block;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #4b515f;
  margin-bottom: 16px;
}

.ChannelInfo--TitleLine {
  margin-top: 13px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.ChannelInfo--Title {
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #5c6374;
}

.ChannelInfo--Subtitle {
  font-size: 12px;
  color: #7e8596;
}

.Channel--Status {
  display: flex;
  flex-basis: 50%;
  justify-content: end;
  color: #1fca10;
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
}

.Channel--Status__Active {
  display: flex;
  margin-right: 6px;
}

>>> .Channel--Status__Active .el-badge__content.is-dot {
  background-color: #1fca10;
  margin-top: 6px;
}

.Channel--Actions {
  margin-top: 24px;
  flex-basis: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>



// WEBPACK FOOTER //
// src/modules/settings/components/ChannelsSection.vue